// Faturas (documentos fiscais emitidos no Vendus).
//
// Desde a migração 064 a fatura é uma entidade com estado próprio (`faturas`)
// e as suas linhas ligam a parcela e a marcação (`faturas_linhas`). Este
// módulo tem a parte pura: estados e a passagem das linhas da BD para a forma
// que o relatório usa. Fica fora do `supabase.js` para ser testável sem rede.

// Estados possíveis de um documento na NOSSA app.
//
// `cancelada` é o estado intermédio incómodo: cancelámos do nosso lado mas o
// documento continua válido no Vendus até lá ser anulado à mão (fase 1). É
// por isso que tem cor de aviso e não de erro — há trabalho por fazer.
export const ESTADOS_FATURA = {
  emitida:        { label: 'Emitida',           cor: '#15803D', descricao: 'Documento válido no Vendus.' },
  cancelada:      { label: 'Cancelada',         cor: '#B45309', descricao: 'Cancelada no sistema — falta anular no Vendus.' },
  anulada_vendus: { label: 'Anulada no Vendus', cor: '#B91C1C', descricao: 'Anulada com nota de crédito. Ciclo fechado.' },
}

export const ORDEM_ESTADOS_FATURA = ['emitida', 'cancelada', 'anulada_vendus']

export const estadoFaturaCfg = (estado) =>
  ESTADOS_FATURA[estado] || { label: estado || '—', cor: '#6B7280', descricao: '' }

// Um documento só conta como receita enquanto estiver vivo.
export const faturaViva = (f) => f?.estado === 'emitida'

// Cancelada do nosso lado mas ainda por anular no Vendus — o que o operador
// tem mesmo de ir lá fazer. É a lista de trabalho que o modelo antigo não
// conseguia produzir, porque o documento desaparecia ao cancelar.
export const porAnularNoVendus = (f) => f?.estado === 'cancelada'

// Converte as linhas devolvidas pelo Supabase (fatura + faturas_linhas, com
// join opcional a agendamentos) na forma usada pelo relatório.
//
// Os dados vivos da marcação ganham à fotografia: o estado tem de ser o ATUAL
// (as permissões filtram por ele) e o serviço pode ter sido renomeado. A
// fotografia entra quando a marcação já não existe — RPC `esquecer_cliente`
// apaga as marcações, e a linha da fatura sobrevive com agendamento_id a NULL.
export function mapearFatura(row) {
  const linhas = row.faturas_linhas || []
  return {
    uid: row.id,
    id: row.vendus_id,
    numero: row.numero || null,
    tipo: row.tipo || null,
    url: row.url || null,
    estado: row.estado || 'emitida',
    data: row.emitida_em,
    emitida_por: row.emitida_por || null,
    cliente: row.cliente_nome || null,
    cliente_id: row.cliente_id || null,
    nif: row.nif || null,
    metodo: row.metodo || null,
    total: Number(row.total || 0),
    lote: !!row.cobranca_id || new Set(linhas.map(l => l.agendamento_id).filter(Boolean)).size > 1,
    cancelada: row.estado === 'cancelada' || row.estado === 'anulada_vendus',
    cancelada_em: row.cancelada_em || null,
    cancelada_por: row.cancelada_por || null,
    cancelada_motivo: row.cancelada_motivo || null,
    nota_credito_id: row.nota_credito_id || null,
    itens: linhas.map(l => {
      const ag = l.agendamentos || null
      return {
        agendamento_id: l.agendamento_id || null,
        // Marcação apagada: fica só a fotografia, e a linha deixa de ter link.
        orfa: !ag,
        quando: ag?.data_hora_inicio || l.quando || null,
        servico: ag?.servicos?.nome || l.descricao || null,
        profissional: ag?.profissionais?.nome || l.profissional_nome || null,
        estado: ag?.estado || null,
        parcela: l.parcela,
        valor: Number(l.valor || 0),
      }
    }).sort((a, b) => String(a.quando || '').localeCompare(String(b.quando || ''))),
  }
}

export const mapearFaturas = (rows) => (rows || []).map(mapearFatura)

// Linhas do documento que um perfil pode ver. Uma linha sem estado (marcação
// já apagada) é sempre visível: não há estado por onde a esconder, e escondê-la
// faria o total do documento deixar de fechar sem explicação.
export function itensVisiveis(fatura, estadosVisiveis) {
  return (fatura.itens || []).filter(i => !i.estado || estadosVisiveis.has(i.estado))
}

// Uma fatura entra na lista se alguma das suas marcações estiver num estado
// visível. `estadosAtivos` junta a permissão do papel ao filtro do ecrã.
export function faturaVisivel(fatura, estadosAtivos) {
  return (fatura.itens || []).some(i => !i.estado || estadosAtivos.has(i.estado))
}

// Nº de marcações distintas cobertas por um conjunto de linhas.
export const contarMarcacoes = (itens) =>
  new Set((itens || []).map(i => i.agendamento_id).filter(Boolean)).size
